import { useCallback, useState } from 'react';
import { useKeyPress } from 'src/hooks/useKeyPress';

/**
 * Hook for managing shift+select functionality in filter checkboxes.
 * Allows users to select a range of checkboxes by clicking one,
 * holding shift, and clicking another.
 */
export const useFilterShiftSelect = () => {
  const isShiftPressed = useKeyPress('Shift');
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  /**
   * Handles checkbox click with shift+select support
   *
   * @param clickedIndex - Index of the clicked checkbox
   * @param clickedKey - Key of the clicked checkbox
   * @param currentValue - Current selected values array
   * @param sortedKeys - All available option keys in sorted order
   * @param onChange - Callback to update selected values
   */
  const handleCheckboxClick = useCallback(
    (
      clickedIndex: number,
      clickedKey: string,
      currentValue: string[],
      sortedKeys: string[],
      onChange: (newValue: string[]) => void
    ) => {
      const isCurrentlySelected = currentValue.includes(clickedKey);

      if (!isShiftPressed || lastClickedIndex === null) {
        // Normal click - toggle single item
        if (isCurrentlySelected) {
          onChange(currentValue.filter((v) => v !== clickedKey));
        } else {
          onChange([...currentValue, clickedKey]);
        }
        setLastClickedIndex(clickedIndex);
      } else {
        // Shift+click - select range
        const startIndex = Math.min(lastClickedIndex, clickedIndex);
        const endIndex = Math.max(lastClickedIndex, clickedIndex);

        // Get all keys in the range from the sorted keys array
        const keysInRange = sortedKeys.slice(startIndex, endIndex + 1);

        // Determine the action based on the clicked item's state
        if (isCurrentlySelected) {
          // If clicked item is selected, deselect the range
          const newValue = currentValue.filter((v) => !keysInRange.includes(v));
          onChange(newValue);
        } else {
          // If clicked item is not selected, select the range
          // Merge with existing selection, avoiding duplicates
          const newValue = [...new Set([...currentValue, ...keysInRange])];
          onChange(newValue);
        }

        // Update the last clicked for next shift+click
        setLastClickedIndex(clickedIndex);
      }
    },
    [isShiftPressed, lastClickedIndex]
  );

  return {
    handleCheckboxClick,
  };
};
